import { yupResolver } from '@hookform/resolvers/yup';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Link } from '@/components/Link';
import { Checkbox } from '@/components/form/Checkbox';
import { InputItem } from '@/components/form/Input/InputItem';
import { SelectItem, SelectOption } from '@/components/form/Select';
import { TextareaItem } from '@/components/form/Textarea/TextareaItem';
import useNearestLocation from '@/hooks/useNearestLocation';
import {
  LocationPhases,
  SimpleIndustriousLocationSearchDto,
} from '@/lib/locations/dto';
import { SEATS_OPTIONS } from '@/utils/constants';
import { getIndustryOptions } from '@/utils/industries';
import { mapLocationToOption } from '@/utils/locations';
import { sortByLabel } from '@/utils/select-option-utils';

import { ApplicationFormData } from '../../types';
import Form from '../shared/Form';
import SubmitButton from '../shared/SubmitButton';
import { ApplicationFormType, formSchema } from './form-schema';

import clsx from 'clsx';

type ApplicationFormProps = {
  locations: SimpleIndustriousLocationSearchDto[];
  locationSlugs?: string[];
  phases?: LocationPhases[];
  promoCode?: string;
  showPromoCode?: boolean;
  isVertical?: boolean;
  onSubmit: (form: ApplicationFormData) => void;
  onChange: (data: ApplicationFormData) => void;
};

export default function ApplicationForm({
  locations,
  locationSlugs,
  phases,
  promoCode,
  showPromoCode,
  isVertical,
  onSubmit,
  onChange,
}: ApplicationFormProps) {
  const t = useTranslations('general.form');
  const locale = useLocale();
  const industryOptions = getIndustryOptions(locale as 'fr' | 'en' | 'nl');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { isValid, errors },
  } = useForm<ApplicationFormType>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      industry: '',
      teamSize: '',
      textMessageOptIn: false,
    },
  });

  const watchAllFields = watch();

  const locationOptions: SelectOption[] = locations
    .map(mapLocationToOption)
    .sort(sortByLabel);

  useNearestLocation({
    locale,
    slugs: locationSlugs,
    phases,
    onChange: async (nearestLocation) => {
      setValue('locationSfId', nearestLocation.sfId);
    },
  });

  const getApplicationFormData = (form: ApplicationFormType) => {
    const { locationSfId, ...otherProps } = form;

    return {
      ...otherProps,
      location: locations.find((x) => x.sfId === locationSfId)!,
      promoCode,
    };
  };

  useEffect(() => {
    onChange(getApplicationFormData(getValues()));
  }, [watchAllFields]);

  const handleFormSubmission = (form: ApplicationFormType) => {
    onSubmit(getApplicationFormData(form));
  };

  return (
    <Form isVertical={isVertical} onSubmit={handleSubmit(handleFormSubmission)}>
      <InputItem
        {...register('firstName')}
        label={t('firstName')}
        error={errors.firstName && t(errors.firstName.message)}
        data-testid="input-firstname"
        required
      />
      <InputItem
        label={t('lastName')}
        {...register('lastName')}
        error={errors.lastName && t(errors.lastName.message)}
        data-testid="input-lastname"
        required
      />
      <InputItem
        label={t('email')}
        {...register('email')}
        error={errors.email && t(errors.email.message)}
        data-testid="input-email"
        required
      />
      <InputItem
        label={t('phone')}
        {...register('phone')}
        error={errors.phone && t(errors.phone.message)}
        data-testid="input-phone"
        required
      />
      <InputItem
        label={t('company')}
        {...register('company')}
        error={errors.company && t(errors.company.message)}
        data-testid="input-company-name"
        required
      />
      <div id="select-industry">
        <SelectItem
          label={t('industry')}
          options={industryOptions}
          {...register('industry')}
          data-testid="select-industry"
          required
        />
      </div>
      <SelectItem
        className="col-span-2"
        label={t('location')}
        options={locationOptions}
        {...register('locationSfId')}
        data-testid="select-location"
        required
      />
      <SelectItem
        label={t('teamSize')}
        options={SEATS_OPTIONS}
        {...register('teamSize')}
        data-testid="select-seats"
        required
      />
      {showPromoCode && (
        <InputItem
          label={t('promoCode')}
          placeholder={promoCode && t('promoCode-placeholder', { promoCode })}
          {...register('promoCode')}
          error={errors.promoCode && t(errors.promoCode.message)}
          data-testid="input-promo-code"
          disabled={!!promoCode}
        />
      )}
      <TextareaItem
        className={clsx(!isVertical && 'md:col-span-2')}
        label={t('comments')}
        {...register('comments')}
        error={errors.comments && t(errors.comments.message)}
        data-testid="input-comments"
      />

      <p
        className={clsx('text-xxs', !isVertical && 'md:col-span-2')}
        data-testid="terms-privacy-policy-description"
      >
        {t.rich('terms-conditions-privacy-policy', {
          termsLink: (chunks) => (
            <Link
              className="underline"
              href="/site-portal-terms"
              rel="noopener"
              target="_blank"
            >
              {chunks}
            </Link>
          ),
          pricacyPolicyLink: (chunks) => (
            <Link
              className="underline"
              href="/privacy-policy"
              rel="noopener"
              target="_blank"
            >
              {chunks}
            </Link>
          ),
        })}
      </p>
      <Checkbox
        id="textMessageOptIn"
        {...register('textMessageOptIn')}
        className={clsx('text-xxs', !isVertical && 'md:col-span-2')}
        data-testid="sms-alert-checkbox"
        label={t('textMessageOptIn')}
      />
      <SubmitButton disabled={!isValid} isVertical={isVertical}>
        {t('submit')}
      </SubmitButton>
    </Form>
  );
}
